/**
 * Manual Gvia (payment collection) WhatsApp alert
 * Fired in the background when an agent enters a manual amount in the Gvia screen
 * (GviaPaymentDetails.vue, on blur of the manual-amount input).
 * Recipients are configured in the "התראת גביית סכום ידנית" WhatsApp section
 * (Firestore: Utils/WhatsAppMsgAdmin/Sections/manualGviaAlert), managed at /app/admin/emails.
 */
import { getWhatsappRecipients } from '@core/services/whatsappSections.js'
import { useWhatsapp } from '@core/composables/useWhatsapp.js'

export const MANUAL_GVIA_ALERT_SECTION = 'manualGviaAlert'

// The WhatsApp API server only supports image+caption sends (/send-images),
// so the alert text rides as the caption of the public GoFood logo.
const LOGO_URL =
  'https://firebasestorage.googleapis.com/v0/b/gofood-il.appspot.com/o/logos%2Flogo_300.png?alt=media&token=21d7d83a-7562-462e-b340-24265c9fa915'

const ILS = new Intl.NumberFormat('he-IL', { style: 'currency', currency: 'ILS' })

/**
 * Build the alert caption (WhatsApp *bold* markup, RTL Hebrew)
 * @param {Object} d
 * @param {string} d.agentName
 * @param {string} d.agentNumber
 * @param {string} d.clientName
 * @param {string} d.clientNum
 * @param {number} d.selectedAmount - Sum of currently-selected invoices (סכום לתשלום)
 * @param {number} d.totalDebt - Client's total open balance
 * @param {number} d.manualAmount - The manually entered amount
 * @param {boolean} [d.isTest] - Marks the message as a test send
 * @returns {string}
 */
export function buildManualGviaAlertCaption({
  agentName,
  agentNumber,
  clientName,
  clientNum,
  selectedAmount,
  totalDebt,
  manualAmount,
  isTest = false,
}) {
  const header = `\u{1F514} *התראת גבייה — סכום ידני*${isTest ? ' (בדיקה)' : ''}`
  const agentLine = `סוכן: ${agentName || '—'}${agentNumber ? ` (${agentNumber})` : ''}`
  const clientLine = `לקוח: ${clientName || '—'}${clientNum ? ` (${clientNum})` : ''}`
  const amounts = [
    `סכום חשבוניות מסומנות: ${ILS.format(selectedAmount || 0)}`,
    `יתרת חוב כוללת: ${ILS.format(totalDebt || 0)}`,
    `*סכום ידני שהוקלד: ${ILS.format(manualAmount || 0)}*`,
  ].join('\n')
  return `${header}\n\n${agentLine}\n${clientLine}\n\n${amounts}`
}

/**
 * Send the alert to every configured recipient.
 * Never throws on a per-recipient failure (sendToTargets resolves false).
 * @param {Object} details - See buildManualGviaAlertCaption
 * @returns {Promise<{ sent: number, total: number }>}
 */
export async function sendManualGviaAlert(details) {
  const recipients = await getWhatsappRecipients(MANUAL_GVIA_ALERT_SECTION)
  if (!recipients || recipients.length === 0) return { sent: 0, total: 0 }

  const caption = buildManualGviaAlertCaption(details)
  const images = [{ url: LOGO_URL, caption }]
  const { sendToTargets } = useWhatsapp()

  let sent = 0
  for (const recipient of recipients) {
    const ok = await sendToTargets(images, recipient.phone)
    if (ok) sent++
  }
  return { sent, total: recipients.length }
}
